if (!customElements.get('product-card-gallery')) {
  class MuzdanProductCardGallery extends HTMLElement {
    connectedCallback() {
      if (this.galleryInitialised) return;

      const initialise = () => {
        if (!window.Swiper || this.galleryInitialised) {
          return;
        }

        const progress = this.querySelector(
          '.product-card-gallery__progress'
        );

        const actions = this.closest(
          '.product-card__actions'
        );

        const previous = actions?.querySelector(
          '.product-card-gallery__buttons .swiper-button-prev'
        );

        const next = actions?.querySelector(
          '.product-card-gallery__buttons .swiper-button-next'
        );

        this.galleryInitialised = true;

        this.swiperInstance = new window.Swiper(this, {
          slidesPerView: 1,
          spaceBetween: 0,
          speed: 450,
          threshold: 8,
          resistanceRatio: 0.65,
          watchOverflow: true,
          rewind: true,
          preventClicks: true,
          preventClicksPropagation: true,
          touchStartPreventDefault: false,

          navigation: {
            prevEl: previous,
            nextEl: next
          },

          pagination: {
            el: progress,
            type: 'progressbar'
          },

          on: {
            sliderFirstMove: () => {
              this.dataset.swiping = 'true';
            },

            touchEnd: () => {
              window.setTimeout(() => {
                delete this.dataset.swiping;
              }, 250);
            }
          }
        });

        /*
         * Desktop hover preview:
         * show the second image while hovered.
         * Three seconds after the pointer leaves,
         * always return to the featured image.
         */
        const productCard = this.closest('product-card');
        const desktopHover = window.matchMedia(
          '(min-width: 750px) and (hover: hover) and (pointer: fine)'
        );
        const hoverTarget = productCard || this;

        const startHoverPreview = () => {
          if (
            !desktopHover.matches ||
            this.swiperInstance.slides.length < 2
          ) {
            return;
          }

          if (this.hoverPreviewTimer) {
            window.clearTimeout(this.hoverPreviewTimer);
            this.hoverPreviewTimer = null;
          }

          if (this.swiperInstance.activeIndex === 0) {
            this.swiperInstance.slideTo(1, 450);
          }
        };

        const leaveHoverPreview = () => {
          if (!desktopHover.matches) return;

          if (this.hoverPreviewTimer) {
            window.clearTimeout(this.hoverPreviewTimer);
          }

          this.hoverPreviewTimer = window.setTimeout(() => {
            this.swiperInstance.slideTo(0, 450);
            this.hoverPreviewTimer = null;
          }, 3000);
        };

        hoverTarget.addEventListener(
          'mouseenter',
          startHoverPreview
        );

        hoverTarget.addEventListener(
          'mouseleave',
          leaveHoverPreview
        );

        /*
         * Prevent a completed swipe from opening
         * the product page.
         */
        this.addEventListener(
          'click',
          event => {
            if (this.dataset.swiping === 'true') {
              event.preventDefault();
              event.stopPropagation();
            }
          },
          true
        );

        /*
         * The controls now sit outside the product
         * link. Stop bubbling to any card-level
         * listener without blocking Swiper.
         */
        [previous, next].forEach(button => {
          if (!button) return;

          button.addEventListener('click', event => {
            event.stopPropagation();
          });
        });
      };

      if (window.Swiper) {
        initialise();
      } else {
        window.addEventListener('load', initialise, {
          once: true
        });
      }
    }

    disconnectedCallback() {
      if (this.hoverPreviewTimer) {
        window.clearTimeout(this.hoverPreviewTimer);
        this.hoverPreviewTimer = null;
      }
    }
  }

  customElements.define(
    'product-card-gallery',
    MuzdanProductCardGallery
  );
}

/*
 * Add a progress bar to Release's existing
 * product-page media slider.
 */
const initialiseProductPageProgress = () => {
  document
    .querySelectorAll('.swiper--product-gallery')
    .forEach(gallery => {
      if (gallery.dataset.muzdanProgress === 'true') {
        return;
      }

      const wrapper = gallery.querySelector(
        '.swiper-wrapper'
      );

      if (!wrapper) return;

      gallery.dataset.muzdanProgress = 'true';

      const progress = document.createElement('div');
      progress.className =
        'muzdan-product-page-progress';

      const fill = document.createElement('span');
      fill.className =
        'muzdan-product-page-progress__fill';

      progress.appendChild(fill);
      gallery.appendChild(progress);

      const updateProgress = () => {
        const slides = Array.from(
          wrapper.querySelectorAll('.swiper-slide')
        ).filter(slide => {
          return !slide.classList.contains(
            'swiper-slide-duplicate'
          );
        });

        if (!slides.length) return;

        const active = wrapper.querySelector(
          '.swiper-slide-active'
        );

        const activeIndex = Math.max(
          slides.indexOf(active),
          0
        );

        const percentage =
          ((activeIndex + 1) / slides.length) * 100;

        fill.style.width = `${percentage}%`;
      };

      updateProgress();

      wrapper.addEventListener(
        'transitionend',
        updateProgress
      );

      gallery.addEventListener(
        'touchend',
        () => {
          window.setTimeout(updateProgress, 50);
        },
        { passive: true }
      );

      gallery.addEventListener(
        'pointerup',
        () => {
          window.setTimeout(updateProgress, 50);
        },
        { passive: true }
      );

      gallery.addEventListener('click', () => {
        window.setTimeout(updateProgress, 50);
      });
    });
};

if (document.readyState === 'loading') {
  document.addEventListener(
    'DOMContentLoaded',
    initialiseProductPageProgress
  );
} else {
  initialiseProductPageProgress();
}

document.addEventListener(
  'shopify:section:load',
  initialiseProductPageProgress
);

/*
 * Preserve Release's variant-media navigation
 * when using the vertical desktop gallery.
 */
const initialiseVerticalVariantNavigation = () => {
  document
    .querySelectorAll('swiper-product-gallery')
    .forEach(gallery => {
      if (
        gallery.dataset.muzdanVariantNavigation ===
        'true'
      ) {
        return;
      }

      const wrapper = gallery.querySelector(
        '.swiper--product-gallery .swiper-wrapper'
      );

      if (!wrapper) return;

      gallery.dataset.muzdanVariantNavigation =
        'true';

      let currentActive = wrapper.querySelector(
        '.swiper-slide-active'
      );

      const scrollToSelectedMedia = () => {
        const nextActive = wrapper.querySelector(
          '.swiper-slide-active'
        );

        if (
          !nextActive ||
          nextActive === currentActive
        ) {
          return;
        }

        currentActive = nextActive;

        /*
         * Release handles the horizontal movement
         * normally on mobile.
         */
        if (
          !window.matchMedia(
            '(min-width: 990px)'
          ).matches
        ) {
          return;
        }

        const reducedMotion = window.matchMedia(
          '(prefers-reduced-motion: reduce)'
        ).matches;

        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            nextActive.scrollIntoView({
              behavior: reducedMotion
                ? 'auto'
                : 'smooth',
              block: 'start',
              inline: 'nearest'
            });
          });
        });
      };

      const observer = new MutationObserver(
        mutations => {
          const activeChanged = mutations.some(
            mutation =>
              mutation.type === 'attributes' &&
              mutation.attributeName === 'class'
          );

          if (activeChanged) {
            scrollToSelectedMedia();
          }
        }
      );

      observer.observe(wrapper, {
        subtree: true,
        attributes: true,
        attributeFilter: ['class']
      });
    });
};

if (document.readyState === 'loading') {
  document.addEventListener(
    'DOMContentLoaded',
    initialiseVerticalVariantNavigation
  );
} else {
  initialiseVerticalVariantNavigation();
}

document.addEventListener(
  'shopify:section:load',
  initialiseVerticalVariantNavigation
);
/*
 * Disable the Featured Products outer carousel
 * on mobile. Individual product-card galleries
 * remain swipeable.
 */
const updateFeaturedProductsSlider = () => {
  const mobile = window.matchMedia(
    '(max-width: 749px)'
  ).matches;

  document
    .querySelectorAll(
      '.section-featured-products ' +
      '.card-product-slider'
    )
    .forEach(slider => {
      const outerSlider = slider.swiper;

      if (outerSlider) {
        if (mobile) {
          outerSlider.disable();
          outerSlider.setTranslate(0);
        } else {
          outerSlider.enable();
          outerSlider.update();
        }
      }

      /*
       * Recalculate each card’s image gallery
       * after its card becomes half-width.
       */
      window.requestAnimationFrame(() => {
        slider
          .querySelectorAll(
            'product-card-gallery'
          )
          .forEach(gallery => {
            const innerSlider =
              gallery.swiperInstance ||
              gallery.swiper;

            if (innerSlider) {
              innerSlider.update();
            }
          });
      });
    });
};

window.addEventListener('load', () => {
  updateFeaturedProductsSlider();

  /*
   * Allows time for Release to initialise Swiper.
   */
  window.setTimeout(
    updateFeaturedProductsSlider,
    500
  );
});

window.addEventListener(
  'resize',
  updateFeaturedProductsSlider
);

document.addEventListener(
  'shopify:section:load',
  () => {
    window.setTimeout(
      updateFeaturedProductsSlider,
      100
    );
  }
);